import React from "react";
import { Factory, UserPlus, MessageSquare, ShieldCheck } from "lucide-react";

const Card = ({ className = "", dark, children, testid }) => (
  <div data-testid={testid}
    className={`relative overflow-hidden rounded-3xl border p-8 transition-colors duration-300 ${dark ? "border-slate-900 bg-[#002855] text-white" : "border-stone-200 bg-white shadow-sm hover:border-orange-300"} ${className}`}>
    {children}
  </div>
);

const IconBox = ({ icon: Icon, dark }) => (
  <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${dark ? "bg-[#FF4500]/20 text-orange-300" : "bg-orange-50 text-[#FF4500]"}`}><Icon className="h-6 w-6" /></div>
);

const Chip = ({ children, accent }) => (
  <span className={`rounded-lg px-3 py-1.5 text-xs ${accent ? "bg-orange-50 font-semibold text-[#c2410c]" : "bg-stone-100 font-medium text-slate-700"}`}>{children}</span>
);

const Title = ({ children, dark }) => <h3 className={`font-heading mt-6 text-2xl font-bold ${dark ? "text-white" : "text-slate-950"}`}>{children}</h3>;
const Body = ({ children, dark }) => <p className={`mt-3 text-sm leading-relaxed sm:text-base ${dark ? "text-slate-300" : "text-slate-600"}`}>{children}</p>;

export const FeaturesBento = () => (
  <section id="features" className="bg-[#fcfaf8] py-20 sm:py-28" data-testid="landing-features">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-2xl text-center">
        <span className="text-xs font-bold uppercase tracking-widest text-[#FF4500]">Built for the Merchant Navy</span>
        <h2 className="font-heading mt-3 text-3xl font-extrabold tracking-tight text-slate-950 sm:text-5xl">Everything a seafarer's career needs</h2>
        <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">From sign-on to shore leave — one community built for the merchant navy.</p>
      </div>

      <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <Card className="lg:col-span-7" testid="bento-jobs">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-start">
            <div className="max-w-md">
              <IconBox icon={Factory} />
              <Title>Post &amp; find sea jobs</Title>
              <Body>Companies post openings by rank and vessel type; seafarers apply in a tap without middlemen or hidden agency fees.</Body>
            </div>
            <span className="self-start rounded-full bg-stone-100 px-3 py-1 text-xs font-semibold text-slate-700">Direct Contracts</span>
          </div>
          <div className="mt-8 flex flex-wrap gap-2 border-t border-stone-100 pt-6">
            <Chip>LNG / LPG Carriers</Chip><Chip>VLCC &amp; Oil Tankers</Chip><Chip>Container Fleets</Chip><Chip accent>1-Tap Sea Application</Chip>
          </div>
        </Card>

        <Card className="lg:col-span-5" testid="bento-follow">
          <IconBox icon={UserPlus} />
          <Title>Follow companies &amp; institutes</Title>
          <Body>Follow the fleets and academies you love and never miss a sign-on window, batch seat availability, or competency course.</Body>
          <div className="mt-8 flex items-center gap-3 border-t border-stone-100 pt-6 text-xs font-semibold text-slate-500">
            <span className="h-2 w-2 rounded-full bg-emerald-500" /> Real-time alerts from RPSL companies
          </div>
        </Card>

        <Card className="lg:col-span-5" testid="bento-communities">
          <IconBox icon={MessageSquare} />
          <Title>Join communities</Title>
          <Body>Talk decks, engine rooms, CoC exams and shore life with crew worldwide. Share port insights and practical troubleshooting.</Body>
          <div className="mt-8 flex flex-wrap gap-2 border-t border-stone-100 pt-6">
            <Chip>Deck Officers</Chip><Chip>Marine Engineers</Chip><Chip>MMD Exams</Chip>
          </div>
        </Card>

        <Card dark className="lg:col-span-7" testid="bento-verified">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-start">
            <div className="max-w-md">
              <IconBox icon={ShieldCheck} dark />
              <Title dark>Verified profiles</Title>
              <Body dark>Show your CDC, rank and sea service so recruiters trust your profile immediately and shortlist you without repetitive paperwork.</Body>
            </div>
            <span className="self-start rounded-full border border-orange-400/30 bg-[#FF4500]/10 px-3.5 py-1 text-xs font-semibold text-orange-300">INDOS &amp; CDC Ready</span>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-3 border-t border-white/10 pt-6 sm:grid-cols-3">
            {[["Document Status", "CDC & CoC Verified"], ["Sea Time Log", "Vessel-by-Vessel"], ["Visibility", "Ready for Sign-On"]].map(([k, v], i) => (
              <div key={k} className="rounded-xl bg-white/5 p-3.5">
                <span className="block text-xs text-slate-400">{k}</span>
                <span className={`mt-1 block text-sm font-semibold ${i === 2 ? "text-emerald-400" : "text-white"}`}>{v}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  </section>
);

export default FeaturesBento;
